<template>
  <section>
    <header class="job-list-header">
      <v-container fluid class="d-flex align-center justify-space-between flex-wrap ga-3 px-4 py-3">
        <section aria-label="Order overview">
          <h3 class="my-0 mt-1">{{ t("text.service_overview") }}</h3>
          <div class="d-flex flex-wrap ga-2 mt-2" style="margin-bottom: 6px; opacity: 0.7;">
            <v-chip size="small" variant="outlined" color="default" style="border-radius: 4px;">
              {{ totalJobCount }} {{ t("text.orders").toLowerCase() }}
            </v-chip>
            <v-chip v-if="openJobCount" size="small" variant="outlined" color="default" style="border-radius: 4px;">
              {{ openJobCount }} {{ t("text.openjobs").toLowerCase() }}
            </v-chip>
          </div>
        </section>

        <nav class="d-flex align-center ga-2" :aria-label="t('text.orders')">
          <v-btn icon="mdi-refresh" variant="text" :loading="refreshing" :title="t('text.refresh')"
            @click="refreshJobs">
          </v-btn>
          <v-btn v-if="!isPhone" color="success" @click="newjobdialog = true">
            <v-icon start>mdi-plus</v-icon>
            {{ t("text.order_new_service") }}
          </v-btn>
        </nav>
      </v-container>
    </header>

    <main aria-live="polite">
      <v-container fluid class="px-4 py-4">
        <section v-if="initialLoading" class="d-flex flex-column ga-3" :aria-label="t('text.loading')">
          <v-skeleton-loader v-for="index in 5" :key="index" type="article" />
        </section>

        <v-alert v-else-if="errorMessage" type="error" variant="tonal" density="comfortable" class="mb-4">
          {{ errorMessage }}
        </v-alert>

        <section v-else-if="jobs.length" class="d-flex flex-column ga-3" :aria-label="t('text.orders')">
          <JobListItem v-for="job in jobs" :key="job.Id" :item="job" :columns-per-row="columnsPerRow"
            @click="openJob(job)" @machine-click="openJob(job)" />
        </section>

        <v-card v-else variant="outlined" class="text-center py-10 px-4">
          <v-icon size="56" color="disabled">mdi-clipboard-text-outline</v-icon>
          <h2 class="text-h6 mt-3 mb-4">{{ t("text.no_items") }}</h2>
          <v-btn color="success" @click="newjobdialog = true">
            <v-icon start>mdi-plus</v-icon>
            {{ t("text.order_new_service") }}
          </v-btn>
        </v-card>

        <section ref="loadMoreTarget" class="job-list-load-more d-flex justify-center align-center mt-2">
          <v-progress-circular v-if="loadingNextPage" indeterminate color="primary" size="26" width="2" />
          <v-btn v-else-if="canLoadMore" variant="text" color="primary" @click="loadNextPage">
            {{ t("text.more") }}
          </v-btn>
        </section>
      </v-container>
    </main>

    <v-btn v-if="isPhone" color="success" icon class="job-list-fab" @click="newjobdialog = true">
      <v-icon>mdi-plus</v-icon>
    </v-btn>

    <router-view />
    <NewJobDialog v-if="newjobdialog" @exit="jobdialogexit" />
    <JobDialog />
    <v-snackbar v-model="toast">{{ toastMessage }}</v-snackbar>
  </section>
</template>


<script>
import http from "@/api/client";
//import CostGraph from "@/components/Cards/CostGraph.vue";
import JobDialog from "@/components/Dialogs/JobDialog.vue";
import NewJobDialog from "@/components/Dialogs/NewJobDialog.vue";
import JobListItem from "@/components/ListItems/JobListItem.vue";
import { serializeToQueryParams } from "@/helpers/appGlobals";
import pinia from "@/store/pinia";
import { useUiStore } from "@/store/pinia/uiStore";

const uiStore = useUiStore(pinia);
const itemsPerPage = 50;

export default {
  name: "JobList",
  components: {
    JobDialog,
    NewJobDialog,
    JobListItem,
    //CostGraph
  },

  data() {
    return {
      toast: false,
      toastMessage: "",
      newjobdialog: false,
      refreshing: false,
      loading: false,
      loadingNextPage: false,
      partialPageReached: false,
      page: 1,
      jobs: [],
      totalJobCount: 0,
      errorMessage: "",
      viewportWidth: typeof window !== "undefined" ? window.innerWidth : 1024,
      loadObserver: null,
    };
  },

  mounted() {
    this.fetchJobs();
    this.observeLoadMoreTarget();
    if (typeof window !== "undefined") {
      window.addEventListener("resize", this.handleResize);
    }
  },

  beforeUnmount() {
    if (this.loadObserver) {
      this.loadObserver.disconnect();
    }
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", this.handleResize);
    }
  },

  methods: {
    t(key) {
      return this.$t ? this.$t(key) : key;
    },
    handleResize() {
      this.viewportWidth = window.innerWidth;
    },
    jobdialogexit(id) {
      if (id) {
        this.toastMessage = "Job created";
        this.toast = true;
        this.openJob({ Id: id });
        this.refreshJobs();
      }
      this.newjobdialog = false;
    },
    openJob(job) {
      if (!job?.Id) return;
      this.$router.push({ name: this.$route.name, params: { ...this.$route.params, jobId: job.Id } });
    },
    async refreshJobs() {
      this.refreshing = true;
      try {
        await this.fetchJobs({ pageNumber: 1, append: false });
      } finally {
        this.refreshing = false;
      }
    },
    async loadNextPage() {
      if (!this.canLoadMore) return;
      await this.fetchJobs({ pageNumber: this.page + 1, append: true });
    },
    async fetchJobs({ pageNumber = 1, append = false } = {}) {
      if (append) {
        this.loadingNextPage = true;
      } else {
        this.loading = true;
      }

      this.errorMessage = "";

      try {
        const filters = {
          ItemsPerPage: itemsPerPage,
          Page: pageNumber,
        };

        const response = await http().get("api2/customer/jobs/filtered" + serializeToQueryParams(filters));
        const pageItems = Array.isArray(response.data?.Items) ? response.data.Items : [];

        this.jobs = append ? this.jobs.concat(pageItems) : pageItems;
        this.page = pageNumber;
        this.partialPageReached = pageItems.length < itemsPerPage;
        this.totalJobCount = response.data?.Total || 0;
      } catch (error) {
        this.errorMessage = error?.response?.data?.Message || "Could not load orders";
        if (!append) this.jobs = [];
      } finally {
        this.loading = false;
        this.loadingNextPage = false;
      }
    },
    observeLoadMoreTarget() {
      const target = this.$refs.loadMoreTarget;
      if (!target || !window.IntersectionObserver) return;

      this.loadObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.loadNextPage();
          }
        },
        { rootMargin: "360px 0px" }
      );

      this.loadObserver.observe(target);
    },
  },

  computed: {
    isPhone() {
      return this.viewportWidth < 600;
    },
    initialLoading() {
      return this.loading && !this.jobs.length;
    },
    canLoadMore() {
      return this.jobs.length > 0 && !this.partialPageReached && !this.loading && !this.loadingNextPage;
    },
    openJobCount() {
      return uiStore.openJobsCount || 0;
    },
    columnsPerRow() {
      if (this.viewportWidth >= 1280) return 3;
      if (this.viewportWidth >= 960) return 2;
      return 1;
    },
  },

  watch: {
    "$route.query.timestamp"() {
      this.refreshJobs();
    },
  },
};
</script>


<style lang="scss" scoped>
.job-list-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: white;
}

.job-list-load-more {
  min-height: 58px;
}

.job-list-fab {
  bottom: 70px;
  position: fixed;
  right: 14px;
  z-index: 20;
}
</style>
